// Helper to build and persist a deterministic AI chat sessionId
// Format: YYYY-MM-DD-<subdomain>-<userId>-<clientHash>-<formHash>-<counter>
// Counter increments only when explicitly requested (e.g., "Start new chat").
const COUNTER_PREFIX = 'ai-chat-session-counter:';
let cleanupDone = false;
let currentCounterKey = null;

function safeGet(storage, key) {
  try {
    return storage.getItem(key);
  } catch (_) {
    return null;
  }
}

function safeSet(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch (_) {
    // ignore quota or access issues
  }
}

function readCounter(counterKey) {
  const fromSession = safeGet(window.sessionStorage, counterKey);
  if (fromSession !== null) return Number(fromSession) || 1;
  const fromLocal = safeGet(window.localStorage, counterKey);
  if (fromLocal !== null) return Number(fromLocal) || 1;
  return 1;
}

function writeCounter(counterKey, value) {
  const val = String(value);
  safeSet(window.sessionStorage, counterKey, val);
  safeSet(window.localStorage, counterKey, val);
}

function normalizePart(input, fallback = '') {
  if (!input && input !== 0) return fallback;
  return String(input).trim() || fallback;
}

function extractSubdomain(hostname) {
  const raw = normalizePart(hostname, '');
  if (!raw) return 'nohost';

  const withoutProtocol = raw.replace(/^[a-z]+:\/\//i, '');
  const hostOnly = withoutProtocol.split('/')[0].split(':')[0];
  if (!hostOnly) return 'nohost';

  const parts = hostOnly.split('.');
  const subdomain = parts[0];
  return normalizePart(subdomain, 'nohost');
}

function buildAiChatSessionId({ userId, clientHash, formHash, hostname } = {}, { increment = false } = {}) {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const datePart = `${yyyy}-${mm}-${dd}`;

  const hostPart = extractSubdomain(hostname ?? (typeof window !== 'undefined' ? window.location.hostname : ''));
  const userPart = normalizePart(userId, 'nouser');
  const clientPart = normalizePart(clientHash, 'noclient');
  // When formHash is absent, keep the segment empty to honour "no value" requirement
  const formPart = normalizePart(formHash, '');

  const counterKey = `${COUNTER_PREFIX}${datePart}:${hostPart}:${userPart}:${clientPart}:${formPart}`;
  currentCounterKey = counterKey;

  // Run cleanup once per load
  cleanupOldKeys(counterKey);

  const current = readCounter(counterKey);
  const next = increment ? current + 1 : current;
  writeCounter(counterKey, next);

  return `${datePart}-${hostPart}-${userPart}-${clientPart}-${formPart}-${next}`;
}

function createNewChatSession() {
  if (!currentCounterKey) return;
  const current = readCounter(currentCounterKey);
  writeCounter(currentCounterKey, current + 1);
  return current + 1;
}

function cleanupOldKeys(activeKey) {
  if (cleanupDone) return;
  cleanupDone = true;

  const storages = [window.sessionStorage, window.localStorage];
  for (const storage of storages) {
    try {
      for (let i = storage.length - 1; i >= 0; i--) {
        const key = storage.key(i);
        // Only target our keys
        if (!key || !key.startsWith(COUNTER_PREFIX)) continue;
        // Skip the currently active key
        if (key === activeKey) continue;

        // Remove anything else (assumed legacy or old date)
        // If it doesn't match the current DATE prefix, remove it.
        const activeDatePart = activeKey.split(':')[1]; // 'ai-chat-session-counter:YYYY-MM-DD:...'
        if (!key.includes(activeDatePart)) {
          try {
            storage.removeItem(key);
          } catch (_) {
            // ignore access issues
          }
        }
      }
    } catch (_) {
      // ignore storage access issues
    }
  }
}

export { buildAiChatSessionId, createNewChatSession };